import { useMutation } from '@tanstack/react-query';

import {
  deleteV1BusinessPartnerCurrentRequestChangeOwnership,
  postV1BusinessPartnerCurrentRequestChangeOwnership,
  postV1RequestChangeOwnershipByIdAccept,
  type ChangeOwnershipRequestPostBody,
} from '../../api/generated/pm';
import { getEtag } from '../../utils/etag';
import {
  invalidateCurrentChangeOwnershipRequest,
  invalidateRequestChangeOwnership,
} from './changeOwnership.queries';

/**
 * Mutation hooks for Change Ownership. See
 * `../Settings/IdentityProviders/identityProviders.mutations.ts` for the pattern.
 */

export const useRequestChangeOwnership = () =>
  useMutation({
    mutationFn: async (payload: ChangeOwnershipRequestPostBody) => {
      const { data } = await postV1BusinessPartnerCurrentRequestChangeOwnership({
        body: payload,
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      invalidateCurrentChangeOwnershipRequest();
    },
  });

export const useCancelChangeOwnershipRequest = () =>
  useMutation({
    mutationFn: async () => {
      const { data } = await deleteV1BusinessPartnerCurrentRequestChangeOwnership({
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      invalidateCurrentChangeOwnershipRequest();
    },
  });

export const useAcceptChangeOwnershipRequest = () =>
  useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      // The accept endpoint requires the request's current etag as If-Match, but
      // its URL differs from the request resource URL (the one the review page
      // GETs), so the shared interceptor can't supply it automatically. Read the
      // etag captured from that GET and pass it explicitly.
      const etag = getEtag(`/api/ciam/pm/v1/requestChangeOwnership/${id}`);
      const { data } = await postV1RequestChangeOwnershipByIdAccept({
        path: { id },
        ...(etag ? { headers: { 'If-Match': etag } } : {}),
        throwOnError: true,
      });
      return data;
    },
    onSuccess: () => {
      invalidateRequestChangeOwnership();
    },
  });
